import React, { useState, useEffect, useRef } from "react";

/**
 * Reusable searchable multi-select dropdown.
 * Matches the SeriesDashboard filter style: dark border trigger, search input,
 * select-all row, scrollable list, clear-all footer.
 *
 * Props
 * ─────
 * label       string   Placeholder shown when nothing is selected
 * options     array    All available option values (strings)
 * value       array    Currently selected values
 * onChange    fn(arr)  Called with the full new selection array
 * minWidth    number   Minimum trigger width (px), default 120
 * displayFn  fn(opt)  Optional: returns display label for an option value
 */
const SearchableMultiSelect = ({
	label,
	options,
	value,
	onChange,
	minWidth = 120,
	displayFn,
}) => {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");
	const containerRef = useRef(null);

	const display = displayFn || ((o) => o);

	const filtered = options.filter((o) =>
		display(o).toString().toLowerCase().includes(search.toLowerCase()),
	);

	// Close on outside click
	useEffect(() => {
		if (!open) return;
		const handler = (e) => {
			if (containerRef.current && !containerRef.current.contains(e.target)) {
				setOpen(false);
				setSearch("");
			}
		};
		document.addEventListener("mousedown", handler);
		return () => document.removeEventListener("mousedown", handler);
	}, [open]);

	const allFilteredSelected =
		filtered.length > 0 && filtered.every((o) => value.includes(o));
	const someFilteredSelected =
		filtered.some((o) => value.includes(o)) && !allFilteredSelected;

	const toggleOption = (opt) => {
		if (value.includes(opt)) {
			onChange(value.filter((v) => v !== opt));
		} else {
			onChange([...value, opt]);
		}
	};

	const toggleSelectAll = (e) => {
		e.stopPropagation();
		if (allFilteredSelected) {
			onChange(value.filter((v) => !filtered.includes(v)));
		} else {
			onChange([...new Set([...value, ...filtered])]);
		}
	};

	const clearAll = (e) => {
		e.stopPropagation();
		onChange([]);
	};

	// ── styles ──────────────────────────────────────────────────────────────────
	const containerStyle = {
		position: "relative",
		minWidth,
		flexShrink: 0,
		fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
	};

	const triggerStyle = {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		padding: "0 8px",
		height: "32px",
		border: `1.5px solid ${open ? "#1e2832" : "#c4c4c4"}`,
		borderRadius: "4px",
		cursor: "pointer",
		backgroundColor: "white",
		fontSize: "0.75rem",
		gap: "4px",
		userSelect: "none",
		transition: "border-color 0.15s",
	};

	const panelStyle = {
		position: "absolute",
		top: "calc(100% + 4px)",
		left: 0,
		zIndex: 9999,
		backgroundColor: "white",
		border: "1.5px solid #1e2832",
		borderRadius: "4px",
		boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
		minWidth: "100%",
		width: "max-content",
		maxWidth: "280px",
	};

	const searchStyle = {
		width: "100%",
		boxSizing: "border-box",
		padding: "6px 8px",
		borderTop: "none",
		borderLeft: "none",
		borderRight: "none",
		borderBottom: "1px solid #e0e0e0",
		fontSize: "0.75rem",
		fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		outline: "none",
	};

	const listStyle = {
		maxHeight: "220px",
		overflowY: "auto",
		padding: "4px 0",
	};

	const itemStyle = (selected) => ({
		display: "flex",
		alignItems: "center",
		gap: "8px",
		padding: "5px 10px",
		fontSize: "0.75rem",
		cursor: "pointer",
		backgroundColor: selected ? "#f0f4f8" : "transparent",
		fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
	});

	return (
		<div ref={containerRef} style={containerStyle}>
			{/* Trigger */}
			<div
				style={triggerStyle}
				onClick={() => {
					setOpen((o) => !o);
					setSearch("");
				}}
			>
				<span
					style={{
						color: value.length > 0 ? "#1e2832" : "#888",
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					}}
				>
					{value.length > 0 ? `${value.length} selected` : label}
				</span>
				<span
					style={{
						display: "flex",
						alignItems: "center",
						gap: "2px",
						flexShrink: 0,
					}}
				>
					{value.length > 0 && (
						<span
							onClick={clearAll}
							title="Clear all"
							style={{
								color: "#cc0000",
								fontWeight: "bold",
								fontSize: "13px",
								lineHeight: 1,
								padding: "0 2px",
								cursor: "pointer",
							}}
						>
							✕
						</span>
					)}
					<span style={{ color: "#666", fontSize: "10px" }}>
						{open ? "▲" : "▼"}
					</span>
				</span>
			</div>

			{/* Dropdown panel */}
			{open && (
				<div style={panelStyle}>
					{/* Search */}
					<input
						autoFocus
						style={searchStyle}
						placeholder={`Search ${label}…`}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						onClick={(e) => e.stopPropagation()}
					/>

					{/* Select-all row */}
					{filtered.length > 0 && (
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: "8px",
								padding: "5px 10px",
								borderBottom: "1px solid #e0e0e0",
								fontSize: "0.72rem",
								fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
								cursor: "pointer",
								backgroundColor: "#f8f8f8",
								userSelect: "none",
							}}
							onClick={toggleSelectAll}
						>
							<input
								type="checkbox"
								readOnly
								checked={allFilteredSelected}
								ref={(el) => {
									if (el) el.indeterminate = someFilteredSelected;
								}}
								style={{ accentColor: "#cc0000", cursor: "pointer", flexShrink: 0 }}
							/>
							<span style={{ color: "#1e2832" }}>
								{allFilteredSelected ? "Deselect all" : "Select all"}
								{search ? ` (${filtered.length} shown)` : ` (${filtered.length})`}
							</span>
						</div>
					)}

					{/* Options list */}
					<div style={listStyle}>
						{filtered.length === 0 ? (
							<div style={{ padding: "8px 10px", fontSize: "0.72rem", color: "#999" }}>
								No options found
							</div>
						) : (
							filtered.map((opt) => {
								const selected = value.includes(opt);
								return (
									<div
										key={opt}
										style={itemStyle(selected)}
										onMouseEnter={(e) =>
											(e.currentTarget.style.backgroundColor = "#f5f5f5")
										}
										onMouseLeave={(e) =>
											(e.currentTarget.style.backgroundColor = selected
												? "#f0f4f8"
												: "transparent")
										}
										onClick={() => toggleOption(opt)}
									>
										<input
											type="checkbox"
											readOnly
											checked={selected}
											style={{ accentColor: "#cc0000", cursor: "pointer", flexShrink: 0 }}
										/>
										<span
											style={{
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											}}
										>
											{display(opt)}
										</span>
									</div>
								);
							})
						)}
					</div>

					{/* Clear-all footer */}
					{value.length > 0 && (
						<div style={{ borderTop: "1px solid #e0e0e0", padding: "5px 10px" }}>
							<span
								onClick={clearAll}
								style={{
									fontSize: "0.72rem",
									color: "#cc0000",
									cursor: "pointer",
									fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
								}}
							>
								Clear all ({value.length})
							</span>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default SearchableMultiSelect;
